/**
 * JURO number ticker
 * ------------------------------------------------------------------
 * Counts a figure up from zero the first time it scrolls into view. Adapted
 * from the "Number Ticker" component on 21st.dev (Dillion Verma).
 *
 * Opt in with data-juro-ticker on the element that holds the number as its
 * first text node, e.g. <dd data-juro-ticker>350<span>g</span></dd>. The real
 * value is in the HTML from the start, so crawlers, screen readers and
 * visitors without JS always get the correct figure; this only animates it.
 *
 * Handles a leading sign (−50, -50) and decimals (3.8), and keeps the same
 * number of decimals the whole way so the width never jumps.
 *
 * Writes textContent on a single text node only. No innerHTML.
 */

(() => {
  // Several sections include this script, so it can execute more than once on
  // a page. Two copies animating one number is how the specs ended on 0: the
  // second copy read the first copy's starting "0" as the target value.
  if (window.juroTickerLoaded) return;
  window.juroTickerLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DURATION = 1400;
  const easeOut = (t) => 1 - Math.pow(1 - t, 4);

  function parse(node) {
    const raw = node.nodeValue;
    const match = raw.match(/^(\s*)([−-]?)(\d+(?:[.,]\d+)?)(.*)$/s);
    if (!match) return null;
    const [, lead, sign, digits, tail] = match;
    const separator = digits.includes(',') ? ',' : '.';
    const decimals = digits.includes(separator) ? digits.split(separator)[1].length : 0;
    return { lead, sign, value: parseFloat(digits.replace(',', '.')), tail, decimals, separator, raw };
  }

  function run(el) {
    const node = [...el.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim());
    if (!node) return;
    const parts = parse(node);
    if (!parts) return;

    const start = performance.now();
    // Pad with figure spaces to the final width. With tabular numerals a figure
    // space is exactly one digit wide, so the unit beside the number never moves.
    const width = parts.value.toFixed(parts.decimals).length;
    const format = (v) =>
      parts.lead +
      parts.sign +
      v.toFixed(parts.decimals).padStart(width, '\u2007').replace('.', parts.separator) +
      parts.tail;

    const step = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      node.nodeValue = format(parts.value * easeOut(t));
      if (t < 1) requestAnimationFrame(step);
      else node.nodeValue = parts.raw;
    };
    node.nodeValue = format(0);
    requestAnimationFrame(step);
  }

  function init() {
    const els = document.querySelectorAll('[data-juro-ticker]:not([data-juro-ticked])');
    if (!els.length || reduced || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.target.hasAttribute('data-juro-ticked')) continue;
          observer.unobserve(entry.target);
          entry.target.setAttribute('data-juro-ticked', '');
          run(entry.target);
        }
      },
      { threshold: 0.6 }
    );
    for (const el of els) observer.observe(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
